import { isArray } from 'lodash';
import { ShopConfigResponse } from '../types/contentful/shopConfig';
import { getEUCountries, isEUCountry } from './countryCodes';
import { CountryCode } from '../types/apollo/generated/types.generated';

export const isEuSiteWithAtLeastOneInternationalDestination = (shopConfig: ShopConfigResponse) => {
  const allowedOriginToDestinationCountries =
    shopConfig.internationalConfig?.allowedOriginToDestinationCountries ?? {};

  return Object.entries(allowedOriginToDestinationCountries).some(
    ([originCountry, destinationCountries]) =>
      isEUCountry(originCountry) &&
      isArray(destinationCountries) &&
      destinationCountries.some((code) => code !== originCountry)
  );
};

/**
 * Determine if a site is strictly a pan-EU site only based on the allowed origin to destination countries.
 *
 * An EU site's origin countries will match all our supported EU countries
 * AND each set of their destination countries will match all our supported EU countries.
 */
export const isPanEUSite = (shopConfig: ShopConfigResponse) => {
  const allowedOriginToDestinationCountries =
    shopConfig.internationalConfig?.allowedOriginToDestinationCountries ?? {};

  const entries = Object.entries(allowedOriginToDestinationCountries);
  const euCountries = getEUCountries();

  if (entries.length === 0) return false;

  const originCountries = entries.map(([origin]) => origin);
  const originCountriesMatchEU =
    originCountries.length === euCountries.size &&
    originCountries.every((originCountry) => euCountries.has(originCountry as CountryCode));

  if (!originCountriesMatchEU) return false;

  // Check that all destination arrays contain exactly all EU countries
  const destinationCountriesMatchEU = entries.every(([, destinations]) => {
    if (!isArray(destinations)) return false;
    return (
      destinations.length === euCountries.size &&
      destinations.every((destination) => euCountries.has(destination as CountryCode))
    );
  });

  return destinationCountriesMatchEU;
};
